import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { fmt$ } from "./shared";

export function ApprovalRulePanel({ refreshKey }) {
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState("");
  const [approvers, setApprovers] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/scm/po-approval-rule").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  if (!data) return null;
  const rule = data.templates.find((t) => t.name === "PO budget approval") || data.templates[0];

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/scm/po-approval-rule", { threshold: Number(threshold), approver_ids: approvers });
      toast.success("PO approval rule active — new POs above the limit will wait for sign-off");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggle = async () => {
    try {
      await api.patch(`/scm/po-approval-rule/${rule.id}?active=${!rule.active}`);
      toast.success(rule.active ? "Approval rule paused" : "Approval rule re-enabled");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startEdit = () => {
    setThreshold(rule?.threshold ?? "");
    setApprovers(rule ? rule.steps.flatMap((s) => s.approver_ids) : data.admins.map((a) => a.id));
    setOpen(true);
  };

  return (
    <div className="border-b border-zinc-950 bg-zinc-50/60 px-5 py-3" data-testid="po-approval-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs">
          <ShieldCheck size={14} className={rule?.active ? "text-emerald-700" : "text-zinc-400"} />
          {rule ? (
            <span data-testid="po-approval-summary">
              <span className={`font-mono text-[10px] px-1.5 py-0.5 mr-2 ${rule.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`}>{rule.active ? "RULE ON" : "RULE PAUSED"}</span>
              POs above <span className="font-semibold tnum">{fmt$(rule.threshold)}</span> need sign-off from <span className="font-semibold">{rule.approvers.join(", ") || "—"}</span> before they can be received
              {data.pending_pos > 0 && <Link to="/approvals" className="ml-2 text-[#FF3E00] hover:underline inline-flex items-center gap-1" data-testid="po-approval-pending-link">{data.pending_pos} awaiting approval <ArrowRight size={10} /></Link>}
            </span>
          ) : (
            <span className="text-zinc-600" data-testid="po-approval-summary">No PO approval rule — anyone can create and receive any PO regardless of value</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono">
          {rule && <button onClick={toggle} className="text-zinc-600 hover:text-zinc-950 hover:underline" data-testid="po-approval-toggle-btn">{rule.active ? "PAUSE" : "ENABLE"}</button>}
          <button onClick={open ? () => setOpen(false) : startEdit} className="bg-zinc-950 text-white px-3 py-1.5 hover:bg-[#FF3E00]" data-testid="po-approval-edit-btn">{open ? "CLOSE" : rule ? "EDIT RULE" : "SET UP APPROVAL RULE"}</button>
        </div>
      </div>
      {open && (
        <form onSubmit={save} className="mt-3 flex flex-wrap items-end gap-4" data-testid="po-approval-form">
          <label className="flex flex-col">
            <span className="label-sm text-zinc-500">Budget limit — POs above this total need approval</span>
            <input type="number" min="0" step="0.01" required value={threshold} onChange={(e) => setThreshold(e.target.value)} className="border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-1 text-sm outline-none tnum w-48" data-testid="po-approval-threshold-input" />
          </label>
          <div className="flex flex-col">
            <span className="label-sm text-zinc-500">Approvers (admins)</span>
            <div className="flex flex-wrap gap-3 py-1">
              {data.admins.map((a) => (
                <label key={a.id} className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={approvers.includes(a.id)} onChange={(e) => setApprovers((s) => (e.target.checked ? [...s, a.id] : s.filter((x) => x !== a.id)))} data-testid={`po-approval-approver-${a.id}`} />
                  {a.name || a.email}
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="bg-zinc-950 text-white text-[10px] font-semibold px-4 py-2 hover:bg-[#FF3E00]" data-testid="po-approval-save-btn">SAVE RULE</button>
        </form>
      )}
    </div>
  );
}
